/**
 * Full-screen ticket image viewer (like admin).
 * Opens image with close and download actions inside — no separate page layout.
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  IconAlertTriangle,
  IconCalendar,
  IconDownload,
  IconMapPin,
  IconTicket,
} from "../../components/Icons.tsx";
import { Loader } from "../../components/Loader.tsx";
import { getTelegram, haptic } from "../../lib/telegram.ts";
import { getAuthHeaders } from "../../lib/graphql.ts";
import { useTicket } from "@/features/tickets/hooks";
import { config } from "../../config.ts";
import { safeParseResponseJson } from "../../lib/json-utils.ts";

type ImageOrientation = "portrait" | "landscape" | "square" | null;

export function TicketPage() {
  const { ticketId } = useParams<{ ticketId: string }>();
  const navigate = useNavigate();
  const tg = getTelegram();

  // URQL hook for ticket data
  const {
    ticket,
    isLoading,
    error: ticketError,
  } = useTicket(ticketId);

  const error = ticketError?.message ?? (!ticket && !isLoading ? "Билет не найден" : null);

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showToolbar, setShowToolbar] = useState(true);
  const [orientation, setOrientation] = useState<ImageOrientation>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  const handleClose = useCallback(() => {
    haptic("light");
    navigate(-1);
  }, [navigate]);

  // BackButton for Mini App
  useEffect(() => {
    if (!tg) return;
    tg.BackButton.show();
    tg.BackButton.onClick(handleClose);
    return () => {
      tg.BackButton.offClick(handleClose);
      tg.BackButton.hide();
    };
  }, [tg, handleClose]);

  // Load ticket image via API (auth required), set orientation on load
  useEffect(() => {
    if (!ticketId || !ticket) return;

    const abortController = new AbortController();
    const url = `${config.apiUrl}/ticket/${ticketId}/image`;
    fetch(url, { headers: getAuthHeaders(), signal: abortController.signal })
      .then((res) => {
        if (!res.ok)
          throw new Error(
            res.status === 401
              ? "Нужна авторизация"
              : "Не удалось загрузить билет",
          );
        return res.blob();
      })
      .then((blob) => {
        if (abortController.signal.aborted) return;
        const objectUrl = URL.createObjectURL(blob);
        objectUrlRef.current = objectUrl;

        const img = new Image();
        img.onload = () => {
          if (abortController.signal.aborted) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          const aspectRatio = img.width / img.height;
          if (aspectRatio > 1.1) {
            setOrientation("landscape");
          } else if (aspectRatio < 0.9) {
            setOrientation("portrait");
          } else {
            setOrientation("square");
          }
          setImageUrl(objectUrl);
        };
        img.onerror = () => {
          URL.revokeObjectURL(objectUrl);
          if (abortController.signal.aborted) return;
          setImageError("Не удалось загрузить изображение билета");
        };
        img.src = objectUrl;
      })
      .catch((err) => {
        if (abortController.signal.aborted) return;
        // Only log unexpected errors, not abort errors
        if (err instanceof Error && err.name !== "AbortError") {
          setImageError("Не удалось загрузить изображение билета");
        }
      });

    return () => {
      abortController.abort();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
    };
  }, [ticketId, ticket]);

  const handleDownload = async () => {
    if (!ticketId || !ticket || isDownloading) return;
    setIsDownloading(true);
    haptic("light");
    try {
      if (tg?.openLink) {
        const res = await fetch(
          `${config.apiUrl}/ticket/${ticketId}/image-download-url`,
          { method: "POST", headers: getAuthHeaders() },
        );
        if (!res.ok) {
          const err = await safeParseResponseJson<{ error?: string }>(res, "ticket download error");
          throw new Error(err.error || "Не удалось получить ссылку");
        }
        const data: unknown = await res.json();
        if (!data || typeof data !== "object" || !("url" in data) || typeof (data as { url?: unknown }).url !== "string") {
          throw new Error("Некорректный ответ сервера");
        }
        tg.openLink((data as { url: string }).url);
        haptic("success");
      } else if (imageUrl) {
        // Use already-loaded blob URL: create link, click, remove (no revoke — image still displayed)
        const a = document.createElement("a");
        a.href = imageUrl;
        a.download = `bilet-${ticket.ticketNumber}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        haptic("success");
      } else {
        const imageRes = await fetch(
          `${config.apiUrl}/ticket/${ticketId}/image`,
          { headers: getAuthHeaders() },
        );
        if (!imageRes.ok) throw new Error("Не удалось загрузить изображение");
        const blob = await imageRes.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `bilet-${ticket.ticketNumber}.png`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        haptic("success");
      }
    } catch {
      if (tg?.showAlert) tg.showAlert("Не удалось подготовить скачивание");
    } finally {
      setIsDownloading(false);
    }
  };

  const handleImageTap = () => {
    setShowToolbar((v) => !v);
  };

  if (!ticketId) {
    return (
      <div
        className="fixed inset-0 z-50 bg-slate-100 flex items-center justify-center p-4"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="rounded-xl bg-white border border-slate-200/60 p-6 text-center max-w-sm w-full">
          <IconAlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-900 text-[16px] font-medium mb-4">Неверная ссылка</p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full py-2.5 rounded-xl font-medium text-[14px] text-slate-700 bg-slate-100 active:bg-slate-200 border border-slate-200/60"
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  if (isLoading && !ticket) {
    return (
      <div
        className="fixed inset-0 z-50 bg-slate-100 flex items-center justify-center p-4"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="rounded-xl bg-white border border-slate-200/60 p-6 text-center max-w-sm w-full">
          <Loader className="mx-auto mb-3" />
          <p className="text-slate-600 text-[14px]">Загрузка...</p>
        </div>
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div
        className="fixed inset-0 z-50 bg-slate-100 flex items-center justify-center p-4"
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="rounded-xl bg-white border border-slate-200/60 p-6 text-center max-w-sm w-full">
          <IconAlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
          <p className="text-slate-900 text-[16px] font-medium mb-4">{error}</p>
          <button
            type="button"
            onClick={handleClose}
            className="w-full py-2.5 rounded-xl font-medium text-[14px] text-slate-700 bg-slate-100 active:bg-slate-200 border border-slate-200/60"
          >
            Закрыть
          </button>
        </div>
      </div>
    );
  }

  // Full-screen image viewer (like admin): image + overlay toolbar
  return (
    <div
      className="fixed inset-0 z-50 bg-black flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {/* Top bar: close */}
      <div
        className={`absolute top-0 left-0 right-0 z-20 transition-all duration-200 ${
          showToolbar ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-full pointer-events-none"
        }`}
        style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
      >
        <div className="bg-gradient-to-b from-black/70 to-transparent">
          <div className="flex items-center justify-between px-4 py-3">
            <button
              type="button"
              onClick={handleClose}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white active:bg-white/20"
              aria-label="Закрыть"
            >
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                <path d="M19 12H5M12 19L5 12L12 5" />
              </svg>
            </button>
            <span className="text-white/90 text-[14px] font-medium">
              Билет №{ticket?.ticketNumber}
            </span>
            <div className="w-10" />
          </div>
        </div>
      </div>

      {/* Image area */}
      <div className="flex-1 relative overflow-hidden min-h-0">
        {!imageUrl && !imageError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100 p-4">
            <div className="rounded-xl bg-white border border-slate-200/60 p-6 text-center max-w-sm w-full">
              <Loader className="mx-auto mb-3" />
              <p className="text-slate-600 text-[14px]">Загрузка изображения...</p>
            </div>
          </div>
        )}

        {imageError && ticket && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-100 p-4">
            <div className="rounded-xl bg-white border border-slate-200/60 p-6 text-center max-w-sm w-full">
              <IconAlertTriangle className="w-14 h-14 text-amber-500 mx-auto mb-4" />
              <p className="text-slate-900 text-[16px] font-medium mb-4">{imageError}</p>
              <button
                type="button"
                onClick={handleClose}
                className="w-full py-2.5 rounded-xl font-medium text-[14px] text-slate-700 bg-slate-100 active:bg-slate-200 border border-slate-200/60"
              >
                Закрыть
              </button>
            </div>
          </div>
        )}

        {imageUrl && !imageError && (() => {
          const isPortrait =
            orientation === "portrait" ||
            orientation === "square" ||
            orientation === null;
          return isPortrait ? (
            <div
              className="absolute inset-0 overflow-y-auto overscroll-contain bg-slate-100"
              onClick={handleImageTap}
            >
              <img
                src={imageUrl}
                alt={`Билет №${ticket?.ticketNumber}`}
                className="w-full h-auto select-none"
                draggable={false}
              />
              {/* Event data block + Download button below image (native style) */}
              <div className="p-4 pt-0 space-y-4">
                <div className="rounded-xl bg-white border border-slate-200/60 overflow-hidden divide-y divide-slate-100">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <IconTicket className="w-5 h-5 text-slate-400 shrink-0" />
                    <span className="text-[15px] text-slate-900">{ticket?.ticketType.name}</span>
                  </div>
                  <div className="flex items-center gap-3 px-4 py-3">
                    <IconCalendar className="w-5 h-5 text-slate-400 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium text-slate-900 truncate">{ticket?.event.title}</p>
                      <p className="text-[13px] text-slate-500 mt-0.5">
                        {ticket?.event.startsAt
                          ? new Date(ticket.event.startsAt).toLocaleDateString("ru-RU", {
                              weekday: "short",
                              day: "numeric",
                              month: "long",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : ""}
                      </p>
                    </div>
                  </div>
                  {ticket?.event.venue && (
                    <div className="flex items-center gap-3 px-4 py-3">
                      <IconMapPin className="w-5 h-5 text-slate-400 shrink-0" />
                      <span className="text-[14px] text-slate-700">{ticket.event.venue}</span>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload();
                  }}
                  disabled={!imageUrl || isDownloading}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-medium text-[15px] text-white bg-[var(--color-accent)] hover:bg-[var(--color-accent-hover)] active:bg-slate-700 disabled:opacity-50 border-0"
                >
                  <IconDownload className="w-5 h-5" />
                  {isDownloading ? "Скачивание…" : "Скачать"}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="absolute inset-0 flex items-center justify-center bg-black"
              onClick={handleImageTap}
            >
              <img
                src={imageUrl}
                alt={`Билет №${ticket?.ticketNumber}`}
                className="max-w-full max-h-full object-contain select-none"
                draggable={false}
              />
            </div>
          );
        })()}
      </div>

      {/* Bottom bar: download (admin style) */}
      <div
        className={`relative z-20 transition-all duration-200 ${
          showToolbar ? "opacity-100 translate-y-0" : "opacity-0 translate-y-full pointer-events-none"
        }`}
      >
        <div className="bg-gradient-to-t from-black/80 to-black/60 backdrop-blur-sm">
          <div
            className="flex items-center justify-around px-4 py-3"
            style={{
              paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 12px)",
            }}
          >
            <button
              type="button"
              onClick={handleDownload}
              disabled={!imageUrl || isDownloading}
              className="flex flex-col items-center gap-1 px-4 py-2 rounded-xl active:bg-white/10 disabled:opacity-50 min-w-[80px]"
            >
              <IconDownload className="w-6 h-6 text-white" />
              <span className="text-[11px] text-white/80 font-medium">
                {isDownloading ? "Скачивание…" : "Скачать"}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
